import * as React from 'react'
import PropTypes from 'prop-types'
import { useTheme } from '@mui/material/styles'
import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Chip from '@mui/material/Chip'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { SparkLineChart } from '@mui/x-charts/SparkLineChart'
import { areaElementClasses } from '@mui/x-charts/LineChart'
import { CircularProgress, IconButton, Tooltip } from '@mui/material'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import TrendingDownIcon from '@mui/icons-material/TrendingDown'
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat'
import InfoIcon from '@mui/icons-material/Info'

function getDaysInMonth(month, year) {
	const date = new Date(year, month, 0)
	const monthName = date.toLocaleDateString('en-US', {
		month: 'short',
	})
	const daysInMonth = date.getDate()
	const days = []
	let i = 1
	while (days.length < daysInMonth) {
		days.push(`${monthName} ${i}`)
		i += 1
	}
	return days
}

function AreaGradient({ color, id }) {
	return (
		<defs>
			<linearGradient id={id} x1="50%" y1="0%" x2="50%" y2="100%">
				<stop offset="0%" stopColor={color} stopOpacity={0.3} />
				<stop offset="100%" stopColor={color} stopOpacity={0} />
			</linearGradient>
		</defs>
	)
}

AreaGradient.propTypes = {
	color: PropTypes.string.isRequired,
	id: PropTypes.string.isRequired,
}

function StatCard({ title, value, interval, trend, data }) {
	const theme = useTheme()
	const daysInWeek = getDaysInMonth(4, 2024)

	const trendColors = {
		up:
			theme.palette.mode === 'light'
				? theme.palette.success.main
				: theme.palette.success.dark,
		down:
			theme.palette.mode === 'light'
				? theme.palette.error.main
				: theme.palette.error.dark,
		neutral:
			theme.palette.mode === 'light'
				? theme.palette.grey[400]
				: theme.palette.grey[700],
	}

	const labelColors = {
		up: 'success',
		down: 'error',
		neutral: 'default',
	}

	const color = labelColors[trend]
	const chartColor = trendColors[trend]
	const trendValues = { up: '+25%', down: '-25%', neutral: '+5%' }

	// Get trend icon based on trend value
	const getTrendIcon = () => {
		switch (trend) {
			case 'up':
				return <TrendingUpIcon sx={{ color: trendColors.up, fontSize: '1.2rem' }} />
			case 'down':
				return <TrendingDownIcon sx={{ color: trendColors.down, fontSize: '1.2rem' }} />
			default:
				return <TrendingFlatIcon sx={{ color: trendColors.neutral, fontSize: '1.2rem' }} />
		}
	}

	// Calculate trend percentage for display
	const getTrendPercentage = () => {
		if (!data || data.length < 2) return null
		const recent = data.slice(-7).reduce((a, b) => a + b, 0) / 7
		const previous = data.slice(-14, -7).reduce((a, b) => a + b, 0) / 7
		if (previous === 0) return null
		const percentage = ((recent - previous) / previous) * 100
		return Math.abs(percentage).toFixed(1)
	}

	const trendPercentage = getTrendPercentage()

	return (
		<Card
			variant="outlined"
			sx={{
				height: '100%',
				flexGrow: 1,
				borderRadius: { xs: '16px', sm: '12px' },
				boxShadow: { xs: '0 4px 20px rgba(0,0,0,0.08)', sm: '0 2px 8px rgba(0,0,0,0.06)' },
				border: 'none',
				background: theme.palette.mode === 'light'
					? 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)'
					: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
				transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
				position: 'relative',
				overflow: 'hidden',
				'&:hover': {
					transform: { xs: 'none', sm: 'translateY(-4px)' },
					boxShadow: { xs: '0 8px 30px rgba(0,0,0,0.12)', sm: '0 8px 25px rgba(0,0,0,0.1)' },
				},
				'&::before': {
					content: '""',
					position: 'absolute',
					top: 0,
					left: 0,
					right: 0,
					height: '4px',
					background: `linear-gradient(90deg, ${chartColor} 0%, ${trendColors.neutral} 100%)`,
				}
			}}
		>
			<CardContent sx={{
				padding: { xs: 2, sm: 2.5 },
				'&:last-child': { paddingBottom: { xs: 2, sm: 2.5 } },
				position: 'relative'
			}}>
				{/* Header Section */}
				<Stack
					direction="row"
					justifyContent="space-between"
					alignItems="flex-start"
					sx={{ mb: 2 }}
				>
					<Box sx={{ flex: 1 }}>
						<Typography
							component="h3"
							variant="subtitle2"
							sx={{
								fontSize: { xs: '0.875rem', sm: '1rem' },
								fontWeight: 600,
								color: 'text.secondary',
								lineHeight: 1.3,
								mb: 0.5
							}}
						>
							{title}
						</Typography>
						{interval && (
							<Typography
								variant="caption"
								sx={{
									color: 'text.disabled',
									fontSize: { xs: '0.75rem', sm: '0.8rem' }
								}}
							>
								{interval}
							</Typography>
						)}
					</Box>

					{/* Trend Icon */}
					<Box sx={{
						display: 'flex',
						alignItems: 'center',
						gap: 0.5,
						ml: 1
					}}>
						{getTrendIcon()}
						{trendPercentage && (
							<Typography
								variant="caption"
								sx={{
									color: chartColor,
									fontWeight: 600,
									fontSize: { xs: '0.75rem', sm: '0.8rem' }
								}}
							>
								{trend === 'up' ? '+' : trend === 'down' ? '-' : ''}{trendPercentage}%
							</Typography>
						)}
					</Box>
				</Stack>

				{/* Value Section */}
				<Box sx={{ mb: 2 }}>
					{value !== "undefined" ? (
						<Typography
							variant="h3"
							component="p"
							sx={{
								fontSize: { xs: '2rem', sm: '2.5rem', md: '2.75rem' },
								fontWeight: 700,
								color: 'text.primary',
								lineHeight: 1.1,
								textAlign: 'center',
								background: theme.palette.mode === 'light'
									? 'linear-gradient(135deg, #2c3e50 0%, #34495e 100%)'
									: 'linear-gradient(135deg, #ecf0f1 0%, #bdc3c7 100%)',
								backgroundClip: 'text',
								WebkitBackgroundClip: 'text',
								WebkitTextFillColor: 'transparent',
								textShadow: '0 2px 4px rgba(0,0,0,0.1)'
							}}
						>
							{value}
						</Typography>
					) : (
						<Box sx={{
							display: 'flex',
							justifyContent: 'center',
							alignItems: 'center',
							height: '60px'
						}}>
							<CircularProgress
								size={40}
								sx={{ color: chartColor }}
							/>
						</Box>
					)}
				</Box>

				{/* Trend Chip */}
				<Box sx={{
					display: 'flex',
					justifyContent: 'center',
					mb: 2
				}}>
					<Chip
						icon={getTrendIcon()}
						label={trendValues[trend]}
						color={color}
						size="small"
						variant="outlined"
						sx={{
							fontWeight: 600,
							fontSize: { xs: '0.75rem', sm: '0.8rem' },
							borderRadius: '20px',
							'& .MuiChip-icon': {
								fontSize: '1rem'
							}
						}}
					/>
				</Box>

				{/* Mini Chart */}
				{data && data.length > 0 && (
					<Box sx={{
						width: '100%',
						height: { xs: '40px', sm: '50px' },
						mt: 'auto'
					}}>
						<SparkLineChart
							colors={[chartColor]}
							data={data}
							area
							showHighlight
							showTooltip
							xAxis={{
								scaleType: 'band',
								data: daysInWeek.slice(0, data.length),
							}}
							sx={{
								[`& .${areaElementClasses.root}`]: {
									fill: `url(#area-gradient-${title.replace(/\s+/g, '-')})`,
								},
								'& .MuiChartsAxis-line': {
									stroke: 'transparent'
								},
								'& .MuiChartsAxis-tick': {
									stroke: 'transparent'
								},
								'& .MuiChartsAxis-label': {
									fill: 'transparent'
								}
							}}
						>
							<AreaGradient
								color={chartColor}
								id={`area-gradient-${title.replace(/\s+/g, '-')}`}
							/>
						</SparkLineChart>
					</Box>
				)}

				{/* Info Tooltip */}
				<Tooltip
					title={`${title}: ${value} | Trend: ${trend} | ${interval}`}
					placement="top"
					arrow
				>
					<IconButton
						size="small"
						sx={{
							position: 'absolute',
							top: 8,
							right: 8,
							color: 'text.disabled',
							'&:hover': {
								color: 'text.secondary',
								backgroundColor: 'action.hover'
							}
						}}
					>
						<InfoIcon fontSize="small" />
					</IconButton>
				</Tooltip>
			</CardContent>
		</Card>
	)
}

StatCard.propTypes = {
	data: PropTypes.arrayOf(PropTypes.number).isRequired,
	interval: PropTypes.string.isRequired,
	title: PropTypes.string.isRequired,
	trend: PropTypes.oneOf(['down', 'neutral', 'up']).isRequired,
	value: PropTypes.string.isRequired,
}

export default StatCard
